import { FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { QueryDataSource, QueryItem } from '../models/DataSource';
import { Storefront } from '../models/StorefrontResponse';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { QueryFilter } from '../models/Blueprint';

interface QueryFieldProps {
    dataSource: QueryDataSource | undefined;
    queryItem: QueryItem;
    whitelistedQueryFields: string[];
    storefront: Storefront
    removeField: (fieldID: string) => void;
    updateFilter:(filter: QueryFilter, queryItemID: string) => void;
}

const QueryFieldForm = (props: QueryFieldProps) => {
    const [fieldName, setFieldName] = useState("");
    const [fieldValue, setFieldValue] = useState<string[]>([]); 
    const [fieldValueStartRange, setFieldValueStartRange] = useState<string>("");
    const [fieldValueEndRange, setFieldValueEndRange] = useState<string>("");
    const [fieldValueMultiple, setFieldValueMultiple] = useState<string>(""); // For fields that don't have a values object
    const [fieldCondition, setFieldCondition] = useState("and");
    const [fieldOmit, setFieldOmit] = useState("false");

    useEffect(() => {
        setFieldName(props.queryItem.field.key);
        setFieldValue(props.queryItem.filter.values || []);
        setFieldCondition(props.queryItem.filter.condition || "and");
        setFieldOmit(props.queryItem.filter.omit?.toString() || "false");

        if (isRangeField(props.queryItem.field.key)) {
            if (props.queryItem.filter.values.length === 1) {
                const rangeParts = props.queryItem.filter.values[0].split("-");

                if (rangeParts.length !== 2) {
                    return;
                }
                setFieldValueStartRange(rangeParts[0]);
                setFieldValueEndRange(rangeParts[1]);
            } else if (props.queryItem.filter.values.length === 2) {
                setFieldValueStartRange(props.queryItem.filter.values[0]);
                setFieldValueEndRange(props.queryItem.filter.values[1]);
            }
        } else {
            console.log("multiple...")
            setFieldValueMultiple(props.queryItem.filter.values.join(","));
        }

    }, [])

    useEffect(() => {
        const filter: QueryFilter = {
            field: fieldName,
            values: createFilterValues(),
            condition: fieldCondition,
            omit: fieldOmit === "true"
        }

        props.updateFilter(filter, props.queryItem.id)
    }, [fieldName, fieldValue, fieldCondition, fieldOmit, fieldValueStartRange, fieldValueEndRange, fieldValueMultiple])

    const handleFieldNameChange = (event: SelectChangeEvent) => {
        setFieldName(event.target.value);
    }

    const handleFieldValueChange = (event: SelectChangeEvent<typeof fieldValue>) => {
        console.log("Field Value: ", event.target.value)
        const {
            target: { value },
          } = event;
          setFieldValue(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
          );
    }

    const handleFieldConditionChange = (event: SelectChangeEvent) => {
        setFieldCondition(event.target.value);
    }

    const handleFieldOmitChange = (event: SelectChangeEvent) => {
        setFieldOmit(event.target.value);
    }

    const rangeStartValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValueStartRange(event.target.value);
    }

    const rangeEndValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValueEndRange(event.target.value);
    }

    const multipleValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValueMultiple(event.target.value);
    }

    const isRangeField = (field: string): boolean => {
        if (!props.dataSource) {
            return false;
        }

        const fieldObj = props.dataSource.fields?.find(f => f.key === field);

        if (!fieldObj) {
            return false;
        }

        return fieldObj?.range || false;
    }

    const getValues = (field: string): { [key: string]: string } => {
        if (!props.dataSource) {
            return {};
        }

        const fieldObj = props.dataSource.fields?.find(f => f.key === field);

        if (!fieldObj) {
            return {};
        }

        return fieldObj?.values;
    }

    const createFilterValues = (): string[] => {
        if (isRangeField(fieldName)) {
            if (!isNaN(Number(fieldValueStartRange)) && !isNaN(Number(fieldValueEndRange))) {
                return [`${fieldValueStartRange}-${fieldValueEndRange}`];
            } else {
                return [fieldValueStartRange,fieldValueEndRange];
            }
        } else if (Object.entries(getValues(fieldName)).length == 0) {
            const items = fieldValueMultiple.split(",").map(function(item) {
                return item.trim();
            });
            console.log("check:", fieldValueMultiple, items)
            return items;
        } else {
            return fieldValue;
        }
    }

    return (
        <Grid sx={{backgroundColor: props.storefront?.activeColors.cart.cartBackground, ml:-2, mt:-2}} component={Paper}>
            <Stack direction="row" spacing={2} sx={{padding:1}}>
                <FormControl fullWidth size="small" sx={{width:300}}>
                    <InputLabel id="trans-type-label">Field Name</InputLabel>
                    <Select labelId="transType" label="Field Name" onChange={handleFieldNameChange} size="small" value={fieldName} MenuProps={{
                                                sx: {
                                                  "&& .Mui-selected": {
                                                    backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                  }
                                                }
                                              }}>
                        {props.whitelistedQueryFields.map((field) => (
                            <MenuItem value={field} key={field}>{field}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {isRangeField(fieldName) ? 
                    <Stack direction={"row"} spacing={1} alignItems="center">
                        <TextField fullWidth size="small" sx={{width:140}} label="Range Start" variant="outlined" value={fieldValueStartRange} onChange={rangeStartValueChanged} />
                        <Typography sx={{}}>-</Typography>
                        <TextField fullWidth size="small" sx={{width:140}} label="Range End" variant="outlined" value={fieldValueEndRange} onChange={rangeEndValueChanged} />
                    </Stack>
                    : Object.entries(getValues(fieldName)).length == 0
                    ? <Tooltip title="Separate multiple values with commas">
                        <TextField fullWidth size="small" sx={{width:300}} label="Values" variant="outlined" value={fieldValueMultiple} onChange={multipleValueChanged} /></Tooltip>
                    : <FormControl fullWidth size="small" sx={{width:300}}>
                    <InputLabel id="values-label">Values</InputLabel>
                        <Select labelId="values" multiple label="Values" onChange={handleFieldValueChange} size="small" value={fieldValue}MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            {Object.entries(getValues(fieldName)).map(([key, value]) => (
                                <MenuItem value={key} key={key}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }

                <Tooltip title="To group items together, specify the same Group number">
                    <FormControl fullWidth size="small" sx={{width:120}}>
                        <InputLabel id="condition-label">Condition</InputLabel>
                        <Select labelId="condition" label="Condition" onChange={handleFieldConditionChange} size="small" value={fieldCondition}MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            <MenuItem value="and" key="and">And</MenuItem>
                            <MenuItem value="0" key="0">Group #1</MenuItem>
                            <MenuItem value="1" key="1">Group #2</MenuItem>
                            <MenuItem value="2" key="2">Group #3</MenuItem>
                        </Select>
                    </FormControl>
                </Tooltip>
                <Tooltip title="Select 'true' to remove records matching this field">
                    <FormControl fullWidth size="small" sx={{width:100}}>
                        <InputLabel id="trans-type-label">Omit</InputLabel>
                        <Select labelId="transType" label="Omit" onChange={handleFieldOmitChange} size="small" value={fieldOmit}MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            <MenuItem value="true" key="true">True</MenuItem>
                            <MenuItem value="false" key="false">False</MenuItem>
                        </Select>
                    </FormControl>
                </Tooltip>
                <IconButton sx={{marginTop:-.5, color:props.storefront.activeColors.cart.cartIconBackground}} size="small" type="button" onClick={() => props.removeField(props.queryItem.id) } >
                    <DeleteForeverIcon />
                </IconButton>
            </Stack>
        </Grid>
    );
};

export default QueryFieldForm;